// Chapter Two Creative, one-pager: what Calvin does, proof, work, and an easy way to reach him.

import React, { useState } from "react";
import { CONTACT, NAV, HERO, PROOF, SERVICES, WORK, ABOUT, FAQ, CONTACT_FORM } from "./content.js";

function Header() {
  return (
    <header className="header">
      <div className="wrap header-inner">
        <a href="#top" className="wordmark">Chapter Two Creative</a>
        <nav className="nav">
          {NAV.map((n) => (
            <a key={n.href} href={n.href}>{n.label}</a>
          ))}
          <a href="#contact" className="btn btn-small">Contact</a>
        </nav>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="hero" id="top">
      <div className="wrap">
        <p className="eyebrow">{HERO.eyebrow}</p>
        <h1>{HERO.heading}</h1>
        <p className="lede">{HERO.body}</p>
        <div className="cta-row">
          <a href="#contact" className="btn">{HERO.primaryCta}</a>
          <a href={`mailto:${CONTACT.email}`} className="link-strong">{CONTACT.email}</a>
        </div>
        <ul className="proof">
          {PROOF.map((p) => (
            <li key={p.big}>
              <strong>{p.big}</strong>
              <span>{p.small}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section className="section" id="services">
      <div className="wrap">
        <h2>{SERVICES.heading}</h2>
        <div className="grid-4">
          {SERVICES.items.map((s) => (
            <article key={s.title} className="card">
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </article>
          ))}
        </div>
        <p className="note">{SERVICES.note}</p>
      </div>
    </section>
  );
}

function Work() {
  const items = WORK.items.filter((w) => !w.hidden);
  return (
    <section className="section" id="work">
      <div className="wrap">
        <h2>{WORK.heading}</h2>
        <div className="work-grid">
          {items.map((w) => (
            <article key={w.name} className="work">
              <a href={w.url} target="_blank" rel="noopener noreferrer" className="work-shot">
                <img src={w.image} alt={`${w.name} website homepage, built by Chapter Two Creative`} loading="lazy" decoding="async" width="1200" height="750" />
              </a>
              <p className="meta">{w.kind} · {w.years}</p>
              <h3>{w.name}</h3>
              <p className="work-body">{w.body}</p>
              <a href={w.url} target="_blank" rel="noopener noreferrer" className="link">
                Visit {w.url.replace(/^https?:\/\/(www\.)?/, "")} ↗
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <section className="section" id="about">
      <div className="wrap about">
        <h2>{ABOUT.heading}</h2>
        <div>
          {ABOUT.body.map((p, i) => <p key={i} className="about-p">{p}</p>)}
          <ul className="tools">
            {ABOUT.tools.map((t) => <li key={t}>{t}</li>)}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section className="section" id="faq">
      <div className="wrap about">
        <h2>{FAQ.heading}</h2>
        <div className="faq">
          {FAQ.items.map((f) => (
            <details key={f.q}>
              <summary><h3>{f.q}</h3></summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactForm() {
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget).entries());
    setState("sending");
    setError("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Could not send right now.");
      setState("sent");
    } catch (err) {
      setError(`${err.message} You can also email ${CONTACT.email}.`);
      setState("error");
    }
  }

  return (
    <section className="section contact" id="contact">
      <div className="wrap contact-inner">
        <div>
          <h2>{CONTACT_FORM.heading}</h2>
          <p className="lede">{CONTACT_FORM.body}</p>
          <p className="contact-direct">
            <a href={`mailto:${CONTACT.email}`} className="link-strong">{CONTACT.email}</a>
            <span>{CONTACT.replyPromise}</span>
          </p>
        </div>

        {state === "sent" ? (
          <p className="thanks" role="status">{CONTACT_FORM.thanks}</p>
        ) : (
          <form className="form" onSubmit={onSubmit}>
            <label>Your name<input name="name" required autoComplete="name" /></label>
            <label>Your email<input name="email" type="email" required autoComplete="email" /></label>
            <label>Your website (optional)<input name="site" placeholder="yourbusiness.com" /></label>
            <label>What do you need?
              <select name="service" defaultValue="">
                <option value="" disabled>Pick one</option>
                {CONTACT_FORM.services.map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
            <label className="full">What's going on?
              <textarea name="message" rows="5" required placeholder="What you want built or fixed, and by when." />
            </label>
            {/* Honeypot: hidden from people, bots fill it. */}
            <input name="website" tabIndex="-1" autoComplete="off" className="hp" aria-hidden="true" />
            {error && <p className="error full" role="alert">{error}</p>}
            <button className="btn full" type="submit" disabled={state === "sending"}>
              {state === "sending" ? CONTACT_FORM.sending : CONTACT_FORM.submit}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="wrap footer-inner">
        <span>© {new Date().getFullYear()} Chapter Two Creative · Calvin To · Las Vegas, NV</span>
        <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <>
      <Header />
      <main>
        <Hero />
        <Services />
        <Work />
        <About />
        <Faq />
        <ContactForm />
      </main>
      <Footer />
    </>
  );
}
